import { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import AdminHeader from "../../components/admin/AdminHeader";
import AdminTestimonialForm from "../../components/admin/AdminTestimonialForm";
import AdminTestimonialsList from "../../components/admin/AdminTestimonialsList";
import useTestimonials from "../../hooks/useTestimonials";
import {
  addTestimonial,
  deleteTestimonial,
  updateTestimonial
} from "../../services/testimonialService";
import { clearAdminSession } from "../../utils/authStorage";

const emptyForm = {
  studentPhoto: "",
  name: "",
  country: "",
  course: "",
  testimonial: ""
};

function AdminTestimonialsPage() {
  const navigate = useNavigate();
  const { testimonials, refresh, isLoading, error: loadError } = useTestimonials();
  const [formValues, setFormValues] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [submitError, setSubmitError] = useState("");
  const [feedback, setFeedback] = useState("");

  const isEditing = Boolean(editingId);

  const handleLogout = () => {
    clearAdminSession();
    navigate("/login", { replace: true });
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormValues((previous) => ({ ...previous, [name]: value }));
  };

  const sanitizedPayload = useMemo(
    () => ({
      studentPhoto: formValues.studentPhoto.trim(),
      name: formValues.name.trim(),
      country: formValues.country.trim(),
      course: formValues.course.trim(),
      testimonial: formValues.testimonial.trim()
    }),
    [formValues]
  );

  const resetForm = () => {
    setFormValues(emptyForm);
    setEditingId(null);
    setSubmitError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFeedback("");
    setSubmitError("");

    if (
      !sanitizedPayload.name ||
      !sanitizedPayload.country ||
      !sanitizedPayload.course ||
      !sanitizedPayload.testimonial
    ) {
      setSubmitError("Please fill all required fields before saving.");
      return;
    }

    try {
      if (isEditing) {
        await updateTestimonial(editingId, sanitizedPayload);
        setFeedback("Testimonial updated successfully.");
      } else {
        await addTestimonial(sanitizedPayload);
        setFeedback("Testimonial added successfully.");
      }

      await refresh();
      resetForm();
    } catch (error) {
      if (error.statusCode === 401) {
        navigate("/login", { replace: true });
        return;
      }
      setSubmitError(error.message || "Could not save testimonial. Please try again.");
    }
  };

  const handleEdit = (record) => {
    setFormValues({
      studentPhoto: record.studentPhoto || "",
      name: record.name || "",
      country: record.country || "",
      course: record.course || "",
      testimonial: record.testimonial || ""
    });
    setEditingId(record.id);
    setSubmitError("");
    setFeedback("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (record) => {
    const confirmed = window.confirm(`Delete testimonial from ${record.name}?`);
    if (!confirmed) {
      return;
    }

    try {
      await deleteTestimonial(record.id);
      await refresh();

      if (editingId === record.id) {
        resetForm();
      }

      setFeedback("Testimonial deleted successfully.");
    } catch (error) {
      if (error.statusCode === 401) {
        navigate("/login", { replace: true });
        return;
      }
      setSubmitError(error.message || "Could not delete testimonial.");
    }
  };

  return (
    <>
      <Helmet>
        <title>Admin Testimonials | NextEra Abroad</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <main className="min-h-screen bg-soft-white px-6 py-8 md:py-10">
        <div className="mx-auto w-full max-w-7xl space-y-6">
          <AdminHeader onLogout={handleLogout} />

          {feedback ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              {feedback}
            </div>
          ) : null}

          {loadError ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {loadError}
            </div>
          ) : null}

          <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr] xl:items-start">
            <AdminTestimonialForm
              formValues={formValues}
              onChange={handleInputChange}
              onSubmit={handleSubmit}
              isEditing={isEditing}
              onCancel={resetForm}
              submitError={submitError}
            />
            <AdminTestimonialsList
              testimonials={testimonials}
              onEdit={handleEdit}
              onDelete={handleDelete}
              isLoading={isLoading}
            />
          </div>
        </div>
      </main>
    </>
  );
}

export default AdminTestimonialsPage;
